<template>
  <div class="main-preview-container" :class="{ 'error-loading': isErrorLoadingImage }">
    <!--
      TODO: Add the background height animation
      :class="{'full-body': isFullBodyCategory}" class="on-load-error"
    -->
    <div
      class="main-preview-height-dictator"
      :class="{ 'full-body': isFullBodyCategory, 'error-loading': isErrorLoadingImage }"
      @transitionend="onTransitionEnd"
    >
      <div
        class="avatar-builder-horizontal-adjuster"
        :class="{ 'offset-preview-image': shouldOffsetPreviewImage }"
      >
        <img
          v-show="!isErrorLoadingImage"
          :key="`${counter}`"
          class="current_preview_image"
          :src="currentPreview"
          :class="{ animating: isAnimating }"
          @load="onPreviewImageLoad"
          @error="onError"
          draggable="false"
        />
        <!-- Show previous image if new one doesn't load -->
        <img
          v-show="isErrorLoadingImage"
          class="current_preview_image"
          :class="{ animating: isAnimating }"
          :src="previousImageURL"
          draggable="false"
        />
      </div>
    </div>
    <div
      v-show="isErrorLoadingImage"
      :class="{ 'full-body': isFullBodyCategory }"
      class="on-load-error"
    >
      <span class="connection-lost-text" v-localize="CONNECTION_LOST_TEXT" />
      <retry-button @on-retry-click="onRetryTap" />
    </div>
  </div>
</template>

<script>
import { LocalizationDirective } from '@/directives/LocalizationDirective';
import RetryButton from './RetryButton.vue';
import Blizzard from '@/services/Blizzard';

export default {
  components: { RetryButton },
  name: 'AvatarBuilderMainPreview',
  props: [
    'isFullBodyCategory', // :Boolean
  ],
  created: function() {
    this.CONNECTION_LOST_TEXT = 'ConnectionLost'; // :string
  },
  data: () => {
    return {
      isAnimating: false, // :boolean
      isErrorLoadingImage: false, // :boolean
      previousImageURL: '', // :string
      counter: 0, // :number
    };
  },
  computed: {
    currentPreview() {
      return this.$store.getters.mainPreview;
    },
    shouldOffsetPreviewImage() {
      // Offset the preview image to the left when the selfie preview is expanded.
      return (
        !this.isFullBodyCategory &&
        !this.$store.getters.isSelfiePreviewDismissed &&
        this.$store.getters.isSelfiePreviewExpanded
      );
    },
  },
  directives: {
    LocalizationDirective,
  },
  methods: {
    onTransitionEnd() {
      // Dispatch an action to the store to signal this animation has completed.
      this.$store.dispatch('updateSelectedCategoryIndex');
    },
    startPreviewImageUpdateAnimation() {
      this.isAnimating = true;
    },
    onPreviewImageLoad() {
      Blizzard.recordFirstMainPreviewLoad();
      this.isAnimating = false;
      this.previousImageURL = this.$store.getters.mainPreview;
      this.isErrorLoadingImage = false;
      this.$store.commit('setUndoRedoButton', { isDisabled: false });
    },
    onError() {
      if (this.previousImageURL) {
        this.isErrorLoadingImage = true;
        this.isAnimating = false;
        this.$store.commit('setUndoRedoButton', { isDisabled: true });
      }
    },
    onRetryTap() {
      this.isAnimating = true;
      this.counter += 1;
    },
  },
  watch: {
    // watch currentPreview from store and call handler function on change
    currentPreview: {
      handler: 'startPreviewImageUpdateAnimation',
      immediate: true,
    },
  },
};
</script>

<style scoped>
.main-preview-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.main-preview-container.error-loading {
  background: rgba(0, 0, 0, 0.5);
}

.main-preview-height-dictator {
  width: 100%;
  position: relative;
  transform-origin: 50% 0;
  -webkit-transform-origin: 50% 0;
  transition: all 0.4s ease-in-out;
  /*
      Main preview image size is dictated by this container's height
    */
  height: 110%;
}

.main-preview-height-dictator.full-body {
  /*
      Transition the main preview and scale it down by shrinking the container.
      Scale its height down from 110% to 48% (--top-half-height), which is the amount of screen real
      estate given to the top half of the builder. That gives 48/110 ~= 0.43 scale factor
    */
  transform: scale(0.43);
  -webkit-transform: scale(0.43);
}

.main-preview-height-dictator.error-loading {
  filter: brightness(50%);
  -webkit-filter: brightness(50%);
}

.avatar-builder-horizontal-adjuster {
  /*
      This container is used to ensure the main preview image stays horizontally
      centered when the image is larger than the parent.
      - 'left: 50%' on its own will set the left edge of the element to be offset by 50%
      of the parent's width.
      - 'transform: translateX(-50%)' will move the element by half its size.
      - Source: https://stackoverflow.com/a/25776315
    */
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  -webkit-transform: translateX(-50%);
  height: 100%;
  transition: left 0.3s;
}

.avatar-builder-horizontal-adjuster.offset-preview-image {
  left: 40%;
}

.current_preview_image {
  height: 100%;
  object-fit: contain;
}

.current_preview_image.animating {
  color: black;
  animation: darken-lighten-image 1.2s infinite linear;
  animation-timing-function: ease-in-out;
  animation-delay: 0.6s;
}

@keyframes darken-lighten-image {
  0% {
    filter: brightness(100%);
    -webkit-filter: brightness(100%);
  }
  50% {
    filter: brightness(50%);
    -webkit-filter: brightness(50%);
  }
  100% {
    filter: brightness(100%);
    -webkit-filter: brightness(100%);
  }
}

.on-load-error {
  position: absolute;
  /* bottom half of web builder is 52% */
  bottom: 52%;
  margin-left: auto;
  margin-right: auto;
  left: 0;
  right: 0;
  text-align: center;
  padding: 30px;
}

.on-load-error.full-body {
  position: absolute;
  /* bottom half of web builder is 52% */
  bottom: 52%;
  left: 0;
  right: 0;
  text-align: center;
  padding: 30px;
}

.connection-lost-text {
  display: inline-block;
  vertical-align: middle;
  text-align: center;
  font-weight: bold;
  font-size: 16px;
  font-family: 'Avenir Next';
  color: #ffff;
  filter: drop-shadow(0pt 1pt 3pt rgba(0, 0, 0, 0.54));
  -webkit-filter: drop-shadow(0pt 1pt 3pt rgba(0, 0, 0, 0.54));
  padding: 15px;
}

@media (orientation: landscape) {
  .main-preview-container {
    height: 200%;
  }
}
</style>
